/* eslint-disable jsx-a11y/alt-text */
import { FC, MouseEventHandler, useState } from 'react';

import Link from 'next/link';
import Image, { StaticImageData } from 'next/image';

import { TColorProp } from 'styled-components';
import { usePrivateLayout } from '~/layouts/private/providers/privateLayout';

import { staticColors } from '~/common/styles/theme';

import { ArrowContainer, ButtonContainer, LoaderWrapper } from './styles';
import { Typography } from '../Typography';
import { Loader } from '../Loader';
import { LinearGradientBorder } from '../LinearGradientBorder';
import { Icon } from '../Icons';
import { Above } from '../Above';

/**

 * @todo - integrar com o componente do tooltip (WIP)
 */

type TButtonsHeight = '48' | '40' | '36' | '32' | '28' | '24' | '20' | '16';

export interface IButtonProps {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  withLinearGradientBorder?: boolean;
  width?: string;
  id?: string;
  circular?: boolean;
  backgroundColor?: string;
  textOrIconColor?: string;
  textOrIconColorOnHover?: string;
  withoutHoverEffect?: boolean;
  borderColor?: string;
  customCssBorderHover?: string;
  withArrowLeft?: boolean;
  withArrowRight?: boolean;
  disabled?: boolean;
  height?: TButtonsHeight;
  iconSize?: number | string;
  icon?: string;
  image?: string | StaticImageData;
  text?: string;
  tooltip?: string;
  fancy?: boolean;
  justifyContent?: string;
  href?: string;
  loading?: boolean;
  loaderColor?: TColorProp;
  type?: 'button' | 'reset' | 'submit';
  loader?: 'puff' | 'bounce' | 'clip' | 'scale' | 'bar';
}

export const Button: FC<IButtonProps> = ({
  id,
  onClick = () => null,
  width = 'min-content',
  image,
  loading = false,
  backgroundColor = staticColors.green[500],
  textOrIconColor = staticColors.grey[1000],
  textOrIconColorOnHover = textOrIconColor,
  borderColor = staticColors.green[500],
  customCssBorderHover,
  withArrowLeft = false,
  withLinearGradientBorder = false,
  circular = false,
  withArrowRight = false,
  disabled = false,
  withoutHoverEffect = false,
  fancy = false,
  type = 'button',
  justifyContent = 'space-between',
  height = '48',
  iconSize,
  icon,
  text,
  href,
  tooltip,
  loader = 'puff',
  loaderColor,
}) => {
  const { isMobile } = usePrivateLayout();
  const [hover, setHover] = useState<boolean>(false);

  const Element = (
    <ButtonContainer
      type={type}
      id={id}
      circular={circular}
      width={width}
      backgroundColor={backgroundColor}
      textOrIconColorOnHover={textOrIconColorOnHover}
      textColor={textOrIconColor}
      borderColor={borderColor}
      disabled={disabled || loading}
      withoutHoverEffect={withoutHoverEffect}
      customCssBorderHover={customCssBorderHover}
      justifyContent={justifyContent}
      fancy={fancy}
      height={`${height}px`}
      onClick={onClick}
      onMouseOver={() => (!withoutHoverEffect ? setHover(true) : null)}
      onMouseLeave={() => setHover(false)}
      style={{
        maxHeight: `${height}px !important`,
        minHeight: `${height}px !important`,
        maxWidth:
          width === 'min-content'
            ? 'max-content !important'
            : `${width} !important`,
        minWidth:
          width === 'min-content'
            ? 'max-content !important'
            : `${width} !important`,
      }}
    >
      {!loading ? (
        <>
          {withArrowLeft && (
            <ArrowContainer id="arrow">
              <Icon
                type="arrow-left-s-line"
                color={hover ? textOrIconColorOnHover : textOrIconColor}
              />
            </ArrowContainer>
          )}
          {icon && (
            <Icon
              type={icon}
              color={hover ? textOrIconColorOnHover : textOrIconColor}
              size={iconSize}
            />
          )}
          {image && <Image alt="img" src={image} width={20} height={20} />}
          {text && <Typography.Label size="medium">{text}</Typography.Label>}
          {withArrowRight && (
            <ArrowContainer id="arrow">
              <Icon
                type="arrow-right-s-line"
                color={hover ? textOrIconColorOnHover : textOrIconColor}
              />
            </ArrowContainer>
          )}
        </>
      ) : (
        <LoaderWrapper>
          <Loader size={Number(height) - 6} type={loader} color={loaderColor} />
        </LoaderWrapper>
      )}
    </ButtonContainer>
  );

  const WithOrWithoutBorder = withLinearGradientBorder ? (
    <LinearGradientBorder
      maxHeight={`${height}px`}
      widthMinContent
      gradientColor={borderColor}
      gradientColorOnHover={borderColor}
    >
      {Element}
    </LinearGradientBorder>
  ) : (
    Element
  );

  const WithOrWithoutTooltip =
    tooltip && !isMobile ? (
      <Above.Tooltip.Simple message={tooltip} background="neutral.950">
        {WithOrWithoutBorder}
      </Above.Tooltip.Simple>
    ) : (
      WithOrWithoutBorder
    );

  return (
    <>
      {href ? (
        <>
          <Link href={href} passHref>
            {WithOrWithoutTooltip}
          </Link>
        </>
      ) : (
        <>{WithOrWithoutTooltip}</>
      )}
    </>
  );
};
